/**
 * Existing backend endpoint (AiController): POST /api/v1/ai/generate-description
 * - Auth: Bearer token, attached (and refreshed on 401) by the shared auth client.
 * - The AI provider key lives on the server only; the browser never sees it.
 * - Success response: { statusType: "SUCCESS", text, searchQueryResponse: "<generated description>" }
 */
export const AI_DESCRIPTION_ENDPOINT = "/api/v1/ai/generate-description";
export const AI_DESCRIPTION_TIMEOUT_MS = 30000;

export class AIDescriptionError extends Error {
  constructor(code, message, status) {
    super(message);
    this.name = "AIDescriptionError";
    this.code = code; // aborted | timeout | rate_limit | auth | network | empty | server | unavailable
    this.status = status; // HTTP status when the server answered
  }
}

// Loaded on first use so importing a form doesn't pull the auth/storage modules in eagerly
let client = null;
const getClient = async () => {
  if (!client) {
    const { createAuthHttpClient } = await import("../features/auth/authHttp");
    client = createAuthHttpClient({ timeout: AI_DESCRIPTION_TIMEOUT_MS });
  }
  return client;
};

const MESSAGES = {
  timeout: "The AI took too long to respond. Please try again.",
  rate_limit: "Too many requests right now. Please wait a moment and try again.",
  auth: "Your session has expired. Please sign in again.",
  network: "Couldn't reach the server. Check your connection and try again.",
  empty: "The AI returned an empty description. Please try again.",
  server: "Couldn't generate a description right now. Please try again.",
  unavailable: "The AI service is temporarily unavailable. Please try again shortly.",
};

const instructionFor = ({ type, fields, tone, language, maxLength, wordRange, action, existingText }) => {
  const details = Object.entries(fields)
    .map(([key, value]) => `${key.replace(/_/g, " ")}: ${Array.isArray(value) ? value.join(", ") : value}`)
    .join("\n");
  const task =
    action === "improve"
      ? "Improve and polish the existing text below using only the details provided."
      : action === "shorter"
      ? "Rewrite the existing text below so it is noticeably shorter."
      : action === "longer"
      ? "Rewrite the existing text below so it is somewhat longer, using only the details provided."
      : "Write a description from the details below.";

  return [
    task,
    `Listing type: ${type}. Tone: ${tone}. Language: ${language}.`,
    `Length: ${wordRange[0]}-${wordRange[1]} words, at most ${maxLength} characters.`,
    "Rules: use ONLY the provided details; do not invent facts, amenities, prices, distances or legal claims (such as RERA approval or no brokerage); suitable for Indian buyers; no exaggeration; plain text only, no markdown, no emojis, no contact details.",
    `Details:\n${details}`,
    existingText ? `Existing text:\n${existingText}` : "",
  ]
    .filter(Boolean)
    .join("\n\n");
};

/**
 * generateDescription({ type, fields, tone, language, maxLength, wordRange, action, existingText }, { signal })
 * Request body: { type, fields, tone, language, maxLength, action, existingText, input }
 *   `input` is the same request rendered as one prompt, because the current backend reads the whole body as text.
 * Resolves with the generated description string.
 */
export const generateDescription = async (request, { signal } = {}) => {
  const payload = {
    type: request.type,
    fields: request.fields,
    tone: request.tone,
    language: request.language,
    maxLength: request.maxLength,
    action: request.action || "generate",
    existingText: request.existingText || undefined,
    input: instructionFor({ ...request, action: request.action || "generate" }),
  };

  try {
    const response = await (await getClient()).post(AI_DESCRIPTION_ENDPOINT, payload, { signal });
    const body = response?.data;
    const text = body?.searchQueryResponse ?? body?.description;
    if (body?.statusType && body.statusType !== "SUCCESS") {
      throw new AIDescriptionError("server", body.text || MESSAGES.server);
    }
    if (typeof text !== "string" || !text.trim()) throw new AIDescriptionError("empty", MESSAGES.empty);
    return text;
  } catch (error) {
    if (error instanceof AIDescriptionError) throw error;
    if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError" || error?.name === "AbortError") {
      throw new AIDescriptionError("aborted", "Cancelled");
    }
    if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
      throw new AIDescriptionError("timeout", MESSAGES.timeout);
    }
    const status = error?.response?.status;
    if (status === 429) throw new AIDescriptionError("rate_limit", MESSAGES.rate_limit);
    if (status === 401 || status === 403) throw new AIDescriptionError("auth", MESSAGES.auth);
    if (!error?.response) throw new AIDescriptionError("network", MESSAGES.network);

    // AiExceptionHandler replies with fixed, client-safe text: { statusType: "ERROR", text }
    const serverText = typeof error.response.data?.text === "string" ? error.response.data.text.trim() : "";
    if (status === 400 && serverText) throw new AIDescriptionError("server", serverText, status);
    if ([502, 503, 504].includes(status)) {
      throw new AIDescriptionError("unavailable", `${serverText || MESSAGES.unavailable} (HTTP ${status})`, status);
    }
    throw new AIDescriptionError("server", `${MESSAGES.server} (HTTP ${status})`, status);
  }
};
